import { useField } from "@taskfortress/core/src/form/hooks"
import React, { useState, useEffect, useCallback, useMemo } from "react"
import { cn } from "../../styles/utils"
import type { PropsWithClassname } from "../../types"
import { Box } from "../Box"
import { Pressable } from "../Pressable"
import { Text } from "../Text"
import { TextInput } from "../form"

interface ColorPickerProps extends PropsWithClassname {
  color: string
  onChange: (color: string) => void
}

// Predefined color palette
const colorOptions = [
  "#f44336", // red
  "#e91e63", // pink
  "#9c27b0", // purple
  "#673ab7", // deep purple
  "#3f51b5", // indigo
  "#2196f3", // blue
  "#03a9f4", // light blue
  "#00bcd4", // cyan
  "#009688", // teal
  "#4caf50", // green
  "#8bc34a", // light green
  "#cddc39", // lime
  "#ffeb3b", // yellow
  "#ffc107", // amber
  "#ff9800", // orange
  "#ff5722", // deep orange
  "#795548", // brown
  "#607d8b", // blue grey
  "#9e9e9e", // grey
  "#000000", // black
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  className,
}) => {
  const [selectedColor, setSelectedColor] = useState(color)
  const [isCustom, setIsCustom] = useState(!colorOptions.includes(color))

  const customColorField = useField({
    name: "customColor",
    initialValue: color,
    validate: (value) => {
      // Basic hex color validation
      if (value && !/^#[0-9A-F]{6}$/i.test(value)) {
        return "Please enter a valid hex color (e.g., #FF0000)"
      }
      return ""
    },
  })

  // Update local state when color prop changes from parent
  useEffect(() => {
    setSelectedColor(color)
    setIsCustom(!colorOptions.includes(color))
  }, [color])

  // Handle predefined color selection
  const handleColorSelect = useCallback(
    (newColor: string) => {
      setSelectedColor(newColor)
      setIsCustom(false)
      onChange(newColor)
    },
    [onChange],
  )

  // Handle custom color text input changes
  const handleCustomColorTextChange = useCallback(
    (value: string) => {
      customColorField.handleChange(value)
      // Only update the color if it's a valid hex color and different from current
      if (value && /^#[0-9A-F]{6}$/i.test(value) && value !== selectedColor) {
        setSelectedColor(value)
        setIsCustom(true)
        onChange(value)
      }
    },
    [customColorField.handleChange, onChange, selectedColor],
  )

  // Memoize grid layout
  const ColorGrid = useMemo(() => {
    return (
      <Box className="flex-row flex-wrap gap-2">
        {colorOptions.map((colorOption) => (
          <Pressable
            key={colorOption}
            className={cn(
              "w-10 h-10 rounded-full border-2",
              selectedColor === colorOption && !isCustom
                ? "border-primary-500"
                : "border-outline-300",
            )}
            style={{ backgroundColor: colorOption }}
            onPress={() => handleColorSelect(colorOption)}
            accessibilityLabel={`Select color ${colorOption}`}
          />
        ))}
      </Box>
    )
  }, [selectedColor, isCustom, handleColorSelect])

  return (
    <Box className={cn("gap-4", className)}>
      {/* Pre-defined color options */}
      {ColorGrid}

      {/* Custom color input */}
      <Box className="gap-2">
        <Text className="text-sm font-medium text-typography-600">
          Custom Color
        </Text>
        <TextInput
          value={customColorField.value}
          handleChange={handleCustomColorTextChange}
          name={customColorField.name}
          error={customColorField.error}
          placeholder="#RRGGBB"
          className="flex-1"
        />
      </Box>
    </Box>
  )
}
